import { Button, TableItem } from 'components/atoms';
import { FormInput, MagellanPopups } from 'components/molecules';
import { QueryFilter, Table, TopBar } from 'components/organisms';
import { TopbarPage } from 'components/templates';
import { useComponentVisible } from 'hooks';
import { useTableColumnsVisibility } from 'hooks/useTableColumnsVisibility';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PiUploadSimple } from 'react-icons/pi';
import { useGetSpotifyWatchlistSearchInfinite } from 'services/spotifyWatchlist';
import { ISpotifyWatchlistSearchItem } from 'services/spotifyWatchlist/types';
import { INodes } from 'services/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { getNodes } from 'utils/table/nodes.utils';

enum WATCHLIST_COLUMNS {
  artistId,
  name,
  fuzzyName,
}

interface IWatchlistFilters {
  artistId: string;
  artistName: string;
  fuzzyName: string;
}

const initialFilters: IWatchlistFilters = {
  artistId: '',
  artistName: '',
  fuzzyName: '',
};

type IWatchlistTableRow = ISpotifyWatchlistSearchItem & { id: string };

const Watchlist = () => {
  const { t } = useTranslation();
  const i18nextPrefix = 'spotifyWatchlist';

  const [filters, setFilters] = useState<IWatchlistFilters>(initialFilters);

  const { ref, isComponentVisible, handleComponentVisible } =
    useComponentVisible<HTMLDivElement>(false);

  const columnsVisibility = useTableColumnsVisibility(
    'SpotifyWatchlistList',
    WATCHLIST_COLUMNS
  );

  const listParams = useMemo(
    () => ({
      artistId: filters.artistId,
      artistName: filters.artistName,
      fuzzyName: filters.fuzzyName,
      itemCount: 25,
    }),
    [filters]
  );

  const { data, isLoading, fetchNextPage, isFetchingNextPage, refetch } =
    useGetSpotifyWatchlistSearchInfinite(listParams);

  const tableData: INodes<IWatchlistTableRow> = useMemo(
    () =>
      getNodes(
        data?.pages.flat().map(item => ({
          ...item,
          id: item.artistId,
        }))
      ),
    [data]
  );

  return (
    <TopbarPage
      topbar={
        <TopBar
          title={t(`${i18nextPrefix}.title.list`)}
          customBackItem={<div />}
          topbarContent={
            <>
              <QueryFilter.ButtonOpener
                i18nextPrefix={i18nextPrefix}
                onClick={handleComponentVisible}
                variant={BUTTON_VARIANT.default}
                isClear
              />
              <Table.CustomColumnsButton
                columns={WATCHLIST_COLUMNS}
                isClear
                visibility={columnsVisibility}
                i18nextPrefix={`${i18nextPrefix}.table`}
                customI18next=''
              />
              <MagellanPopups.WatchlistImport
                onSuccess={() => refetch()}
                trigger={
                  <Button variant={BUTTON_VARIANT.submit}>
                    <Button.Text text={t('button.import')} />
                    <Button.Icon icon={<PiUploadSimple />} />
                  </Button>
                }
              />
            </>
          }>
          <QueryFilter<IWatchlistFilters>
            i18nextPrefix={i18nextPrefix}
            initialValues={filters}>
            <>
              <QueryFilter.Form<IWatchlistFilters>
                ref={ref}
                isOpen={isComponentVisible}
                onDebouncedFormChange={values => setFilters(values)}>
                <QueryFilter.InputsGrid>
                  <FormInput.Text
                    name='artistId'
                    label={t(`${i18nextPrefix}.filters.artistId`)}
                  />
                  <FormInput.Text
                    name='artistName'
                    label={t(`${i18nextPrefix}.filters.artistName`)}
                  />
                  <FormInput.Text
                    name='fuzzyName'
                    label={t(`${i18nextPrefix}.filters.fuzzyName`)}
                  />
                </QueryFilter.InputsGrid>
              </QueryFilter.Form>
              <QueryFilter.Labels isFormOpen={isComponentVisible} />
            </>
          </QueryFilter>
        </TopBar>
      }>
      <Table<IWatchlistTableRow>
        id={columnsVisibility.tableId}
        data={tableData}
        loading={isLoading || isFetchingNextPage}
        onFullyScroll={fetchNextPage}
        columns={[
          {
            label: t(`${i18nextPrefix}.table.artistId`),
            renderCell: ({ artistId }) => (
              <TableItem>
                <TableItem.Text text={artistId} />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(WATCHLIST_COLUMNS.artistId),
          },
          {
            label: t(`${i18nextPrefix}.table.name`),
            renderCell: ({ name }) => (
              <TableItem>
                <TableItem.Text text={name} />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(WATCHLIST_COLUMNS.name),
          },
          {
            label: t(`${i18nextPrefix}.table.fuzzyName`),
            renderCell: ({ fuzzyName }) => (
              <TableItem>
                <TableItem.Text text={fuzzyName} />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(WATCHLIST_COLUMNS.fuzzyName),
          },
        ]}
      />
    </TopbarPage>
  );
};

export default Watchlist;
